import type { BlobStorageRequest, LogicAppsRequest, FunctionsRequest, AppServiceRequest, BandwidthRequest } from '../api/types';

/**
 * Encodes calculator inputs as URL search params.
 * Decodes URL params back to calculator inputs on load.
 * Used by ShareButton and page components.
 */

type ParamValue = string | number | boolean | null | undefined;

export function encodeToURL(
  params: Record<string, ParamValue>,
  baseURL?: string
): string {
  const base = baseURL ?? window.location.href.split('?')[0];
  const url = new URL(base);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
  
  return url.toString();
}

export function decodeFromURL(): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

export function parseURLToBlob(): Partial<BlobStorageRequest> | null {
  const params = decodeFromURL();
  if (!params.tier && !params.storageTB) return null;

  return {
    storageTB: params.storageTB ? Number(params.storageTB) : undefined,
    tier: params.tier as any,
    redundancy: params.redundancy as any,
    writeOpsPerMonth: params.writeOpsPerMonth ? Number(params.writeOpsPerMonth) : undefined,
    readOpsPerMonth: params.readOpsPerMonth ? Number(params.readOpsPerMonth) : undefined,
    listOpsPerMonth: params.listOpsPerMonth ? Number(params.listOpsPerMonth) : undefined,
    gbRetrievedPerMonth: params.gbRetrievedPerMonth ? Number(params.gbRetrievedPerMonth) : undefined,
    enablePITR: params.enablePITR === 'true',
    gbToRestoreIfPITR: params.gbToRestoreIfPITR ? Number(params.gbToRestoreIfPITR) : undefined,
    changeOpsPerMonth: params.changeOpsPerMonth ? Number(params.changeOpsPerMonth) : undefined,
    daysStored: params.daysStored ? Number(params.daysStored) : undefined,
  };
}

/** Flat params for ShareButton — matches parseURLToBlob keys. */
export function blobParamsForShare(inputs: BlobStorageRequest): Record<string, ParamValue> {
  return {
    storageTB: inputs.storageTB,
    tier: inputs.tier,
    redundancy: inputs.redundancy,
    writeOpsPerMonth: inputs.writeOpsPerMonth,
    readOpsPerMonth: inputs.readOpsPerMonth,
    listOpsPerMonth: inputs.listOpsPerMonth,
    gbRetrievedPerMonth: inputs.gbRetrievedPerMonth,
    enablePITR: inputs.enablePITR,
    gbToRestoreIfPITR: inputs.gbToRestoreIfPITR,
    changeOpsPerMonth: inputs.changeOpsPerMonth,
    daysStored: inputs.daysStored,
  }
}

export function parseURLToLogicApps(): Partial<LogicAppsRequest> | null {
  const params = decodeFromURL();
  if (!params.planType && !params.runsPerDay) return null;

  return {
    planType: params.planType as any,
    runsPerDay: params.runsPerDay ? Number(params.runsPerDay) : undefined,
    actionsPerRun: params.actionsPerRun ? Number(params.actionsPerRun) : undefined,
    standardConnectorCallsPerMonth: params.standardConnectorCallsPerMonth ? Number(params.standardConnectorCallsPerMonth) : undefined,
    enterpriseConnectorCallsPerMonth: params.enterpriseConnectorCallsPerMonth ? Number(params.enterpriseConnectorCallsPerMonth) : undefined,
    wsTier: params.wsTier as any,
  };
}

export function parseURLToFunctions(): Partial<FunctionsRequest> | null {
  const params = decodeFromURL();
  if (!params.planType && !params.executionsPerMonth) return null;

  return {
    planType: params.planType as any,
    executionsPerMonth: params.executionsPerMonth ? Number(params.executionsPerMonth) : undefined,
    avgDurationMs: params.avgDurationMs ? Number(params.avgDurationMs) : undefined,
    avgMemoryMB: params.avgMemoryMB ? Number(params.avgMemoryMB) as any : undefined,
    epTier: params.epTier as any,
    instanceCount: params.instanceCount ? Number(params.instanceCount) : undefined,
  };
}

export function parseURLToAppService(): Partial<AppServiceRequest> | null {
  const params = decodeFromURL();
  if (!params.os && !params.tier) return null;

  return {
    os: params.os as any,
    tier: params.tier as any,
    sku: params.sku as any,
    instanceCount: params.instanceCount ? Number(params.instanceCount) : undefined,
    autoscaleEnabled: params.autoscaleEnabled === 'true',
    minInstances: params.minInstances ? Number(params.minInstances) : undefined,
    maxInstances: params.maxInstances ? Number(params.maxInstances) : undefined,
  };
}

export function parseURLToBandwidth(): Partial<BandwidthRequest> | null {
  const params = decodeFromURL();
  if (!params.outboundInternetGB && !params.interRegionGB) return null;

  return {
    outboundInternetGB: params.outboundInternetGB ? Number(params.outboundInternetGB) : undefined,
    interRegionGB: params.interRegionGB ? Number(params.interRegionGB) : undefined,
  };
}
